import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "../api/client";
import { useUser } from "../auth/useUser";
import { Header } from "../components/Header";
import { Modal } from "../components/Modal";
import { Field, Input } from "../components/Field";
import { Table, THead, TH, TBody, TR, TD } from "../components/Table";
import { usePatchPlan } from "../auth/adminMutations";
import type { components } from "../api/types";

type Plan = components["schemas"]["Plan"];

// Plans page (0016 Slice 2). The /plans/{id} collection drives
// per-plan grant amounts at signup. The orchestrator's OAuthCallback
// requires /plans/free to exist (no env-default fallback after the
// migration window closed) -- if Plans is empty here, new signups
// will fail until at least "free" is seeded.
export function Plans() {
  const { data: me } = useUser();
  const q = useQuery({
    queryKey: ["admin", "plans"],
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/admin/plans");
      if (error) throw error;
      return data as { plans: Plan[] };
    },
  });

  // editing = the plan being edited, OR the literal string "new" when
  // the operator clicked "New plan" (modal lets them type an ID).
  const [editing, setEditing] = useState<Plan | "new" | null>(null);

  if (!me) return null;
  const plans = q.data?.plans ?? [];
  const hasFree = plans.some((p) => p.id === "free");

  return (
    <div className="min-h-screen flex flex-col">
      <Header user={me} />
      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-8 py-12">
        <div className="flex items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="text-display font-medium text-ink-50 tracking-tightest mb-2">
              Plans
            </h1>
            <p className="text-sm text-ink-300">
              Per-plan signup grant amounts. The orchestrator looks up{" "}
              <code className="text-ink-100">/plans/free</code> at OAuth
              callback time; new signups fail if the doc is missing.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setEditing("new")}
            className="inline-flex items-center gap-2 rounded-md bg-accent px-3.5 py-2 text-sm font-medium text-ink-950 hover:bg-accent-glow transition-colors"
          >
            New plan
            <span className="opacity-60">+</span>
          </button>
        </div>

        {!hasFree && !q.isLoading && (
          <div
            role="alert"
            className="mb-6 rounded-md border border-amber-700/40 bg-amber-950/30 px-3 py-2 text-xs text-amber-200"
          >
            <strong>No "free" plan defined.</strong> New OAuth signups
            will fail until you create one. Click "New plan" above and
            use <code>free</code> as the ID.
          </div>
        )}

        {q.isLoading && <p className="text-sm text-ink-400">loading…</p>}
        {q.error && (
          <p className="text-sm text-rose-300">
            Failed: {(q.error as Error).message}
          </p>
        )}

        {plans.length > 0 && (
          <Table>
            <THead>
              <TH>id</TH>
              <TH>display_name</TH>
              <TH>grant_amount</TH>
              <TH></TH>
            </THead>
            <TBody>
              {plans.map((p) => (
                <TR key={p.id}>
                  <TD className="font-mono text-[11px]">{p.id}</TD>
                  <TD>{p.display_name}</TD>
                  <TD className="tabular-nums">{p.grant_amount}</TD>
                  <TD>
                    <button
                      onClick={() => setEditing(p)}
                      className="text-xs text-ink-400 hover:text-ink-100"
                    >
                      edit
                    </button>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}

        {plans.length === 0 && !q.isLoading && (
          <p className="text-sm text-ink-400">No plans defined yet.</p>
        )}

        <PlanModal editing={editing} onClose={() => setEditing(null)} />
      </main>
    </div>
  );
}

// PlanModal serves both "create" (editing === "new") and "edit"
// (editing is a Plan). In create mode the plan ID is editable; in
// edit mode it's read-only (the doc ID is the primary key, not
// renameable). PATCH /plans/{plan_id} auto-creates if missing, so
// both paths use the same endpoint.
function PlanModal({
  editing,
  onClose,
}: {
  editing: Plan | "new" | null;
  onClose: () => void;
}) {
  const isNew = editing === "new";
  const existing = isNew ? null : (editing as Plan | null);
  const [planId, setPlanId] = useState(existing?.id ?? "");
  const [name, setName] = useState(existing?.display_name ?? "");
  const [grant, setGrant] = useState<number>(existing?.grant_amount ?? 0);
  const patch = usePatchPlan(planId);

  if (!editing) return null;

  const idError =
    isNew && planId !== "" && !/^[a-z0-9_-]+$/.test(planId)
      ? "plan ID must be lowercase alphanumeric, '-' or '_'"
      : null;
  const canSubmit =
    !idError && planId.trim() !== "" && !patch.isPending;

  return (
    <Modal open onClose={onClose} title={isNew ? "New plan" : `Edit plan: ${planId}`}>
      <Field
        label="Plan ID"
        hint={isNew ? "doc ID for /plans/{id}; e.g. 'free' or 'paid'" : "read-only"}
      >
        <Input
          value={planId}
          onChange={(e) => setPlanId(e.target.value)}
          disabled={!isNew}
          placeholder="free"
        />
      </Field>
      {idError && (
        <p className="text-xs text-rose-300 mb-2">{idError}</p>
      )}
      <Field label="Display name">
        <Input value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Grant amount" hint="0..10000 credits granted at first signup">
        <Input
          type="number"
          min={0}
          max={10000}
          value={grant}
          onChange={(e) => setGrant(Number(e.target.value))}
        />
      </Field>
      {patch.error && (
        <p className="text-xs text-rose-300 mb-2">
          {(patch.error as Error).message}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <button
          onClick={onClose}
          className="text-xs px-3 py-1.5 rounded border border-ink-700 text-ink-300"
        >
          Cancel
        </button>
        <button
          onClick={async () => {
            await patch.mutateAsync({ display_name: name, grant_amount: grant });
            onClose();
          }}
          disabled={!canSubmit}
          className="text-xs px-3 py-1.5 rounded bg-accent text-ink-950 disabled:opacity-50"
        >
          {isNew ? "Create" : "Save"}
        </button>
      </div>
    </Modal>
  );
}
